/** Pure formatting/filtering helpers for the agent log console. */

import type { AgentLog } from '../types'

export type LogLevel = 'I' | 'W' | 'E'

/** Coerce a server level (any string) to one of I/W/E. Garbage → I. */
export function normalizeLevel(raw: string): LogLevel {
  const c = (raw || 'I').charAt(0).toUpperCase()
  return c === 'W' || c === 'E' ? c : 'I'
}

/** Render an ISO timestamp as HH:MM:SS.mmm UTC. Invalid → returned as-is.
 *  UTC, not the admin's browser TZ: the device and the admin can be in
 *  different countries, so a fixed reference avoids misreading device times. */
export function formatLogTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number, w = 2) => String(n).padStart(w, '0')
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}.${p(d.getUTCMilliseconds(), 3)} UTC`
}

/** Tailwind text color per level — error red, warn amber, info neutral. */
export function levelColorClass(level: string): string {
  switch (normalizeLevel(level)) {
    case 'E':
      return 'text-red-400'
    case 'W':
      return 'text-amber-400'
    default:
      return 'text-gray-300'
  }
}

/** Case-insensitive substring match over tag + message. Empty query matches all. */
export function matchesSearch(log: { tag: string; message: string }, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  return log.tag.toLowerCase().includes(q) || log.message.toLowerCase().includes(q)
}

/** Newest-first order mirroring the server's `created_at DESC, seq DESC, id DESC`.
 *  `created_at` is parsed as a date (robust to ISO offset/Z format) rather than
 *  compared lexically. */
function compareNewestFirst(a: AgentLog, b: AgentLog): number {
  const ta = Date.parse(a.created_at)
  const tb = Date.parse(b.created_at)
  if (ta !== tb) return tb - ta
  if (a.seq !== b.seq) return b.seq - a.seq
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0
}

/**
 * Merge the keyset history pages with the live newest-page poll into one
 * chronological (oldest-first) list for the console.
 *
 * Why a merge and not a single infinite-query refetch: refetchInterval on an
 * infinite query re-fetches EVERY loaded page each tick (work scaling with
 * scroll depth) though new rows only ever appear on page 0. The live poll fetches
 * just the newest page; rows are immutable + id-keyed, so a Map dedup (live wins)
 * is exact. Re-sorted by the server's key triple so the merge can't misorder a
 * live row relative to the paged history.
 */
export function mergeChronological(
  pages: { items: AgentLog[] }[] | undefined,
  latest: AgentLog[] | undefined,
  search: string,
): AgentLog[] {
  const byId = new Map<string, AgentLog>()
  for (const p of pages ?? []) for (const l of p.items) byId.set(l.id, l)
  for (const l of latest ?? []) byId.set(l.id, l)
  return [...byId.values()]
    .sort(compareNewestFirst)
    .reverse() // newest-first → oldest-top/newest-bottom console order
    .filter((l) => matchesSearch(l, search))
}

/**
 * True when the live poll window and the frozen history page 0 are DISJOINT
 * with a gap between them — the OLDEST row the live poll currently holds is
 * still strictly newer than the NEWEST row in history page 0. That can only
 * happen when more than a full live page of rows landed since history page 0
 * was last fetched, so rows between the two windows are held by neither and
 * stay invisible until the history head advances (refetch closes the gap).
 *
 * In steady state the two windows overlap on the newest rows, so this returns
 * false. Gating the history refetch on it therefore fires only on a real burst,
 * NOT every poll tick. The naive `liveItems.length >= PAGE_SIZE` gate it
 * replaces was steady-state true for any phone holding a full page of logs, so
 * it re-fetched every loaded keyset page each 5s tick — the scroll-depth-scaling
 * cost `useAgentLogsLatest` exists to avoid.
 */
export function hasLiveHistoryGap(
  historyPage0: AgentLog[] | undefined,
  liveItems: AgentLog[] | undefined,
): boolean {
  const liveOldest = liveItems?.[liveItems.length - 1]
  const historyNewest = historyPage0?.[0]
  if (!liveOldest || !historyNewest) return false
  // < 0 ⟺ liveOldest is strictly newer than historyNewest (same ordering as the
  // server's created_at/seq/id triple) ⟺ the windows don't overlap.
  return compareNewestFirst(liveOldest, historyNewest) < 0
}

/**
 * Decide what LogConsole's scroll effect does with a pending "Load older"
 * anchor (lives here, not in the component, so it's unit-testable without
 * layout — jsdom has no scroll metrics):
 * - 'restore': the top row changed → older rows prepended → restore the offset.
 * - 'clear':   the fetch finished (not loading) but the top row did NOT change
 *              → the older page was empty or fully filtered by search. Release
 *              the anchor so auto-scroll resumes — without this it sticks
 *              forever and the console silently stops following new lines.
 * - 'wait':    the fetch is still in flight; a bottom-append must not consume
 *              the anchor — keep waiting for the real prepend.
 */
export function computeAnchorAction(
  currentTopId: string | undefined,
  anchorId: string,
  loadingOlder: boolean,
): 'restore' | 'clear' | 'wait' {
  if (currentTopId !== anchorId) return 'restore'
  return loadingOlder ? 'wait' : 'clear'
}
